import React, { useCallback, useState } from 'react'

import { createFormHookContexts, useStore } from '@tanstack/react-form'
import { twMerge } from 'tailwind-merge'

import { AssigneeComboboxMulti } from '@/components/common/ComboboxV2/AssigneeCombobox'
import { FormInput } from '@/components/common/TextInputV2'
import ErrorMessage from '@/components/form/ErrorMessage'
import { RichTextDescriptionField } from '@/components/form/RichTextDescriptionField'
import * as EditField from '@/components/library-v2/catalog/ui/Field/Edit'
import { DateSelect } from '@/components/pursuits/v2/components/PursuitForm/DateSelect'
import { StageSelect } from '@/components/pursuits/v2/components/PursuitForm/StageSelect'
import { TagSelect } from '@/components/pursuits/v2/components/PursuitForm/TagSelect'
import { TypeSelect } from '@/components/pursuits/v2/components/PursuitForm/TypeSelect'

import { sanitizeDollarInput } from '@/helpers/string'

import type { UUID } from '@/types/Id'
import type { PursuitType } from '@/types/pursuit/Pursuit'

// This file exports pursuit-specific field controls for use with tanstack/react-form
// These should be used with the useFieldContext hook from createFormHookContexts

interface CreatePursuitFieldControlsArgs {
  useFieldContext: ReturnType<typeof createFormHookContexts>['useFieldContext']
}

const selectRawError = (s: any) => s.meta.errors

const formatWithCommas = (n: number): string => n.toLocaleString('en-US')

const triggerClassName = twMerge(
  'h-9 px-3 text-sm font-normal rounded-md w-full justify-start shadow-none gap-x-2',
  'bg-primary-50 dark:bg-default-dark dark:hover:bg-primary-900 border-primary-200 dark:border-primary-800',
  'hover:bg-primary-100 dark:hover:bg-primary-900',
)

export const createPursuitFieldControls = ({
  useFieldContext,
}: CreatePursuitFieldControlsArgs) => {
  const TitleFieldControl: React.FC = () => {
    const field = useFieldContext<string | undefined>()
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        field.handleChange(e.target.value),
      [field],
    )
    const rawError = useStore(field.store, selectRawError)
    const error = EditField.formatErrorMessage(rawError)
    return (
      <>
        <FormInput
          value={field.state.value}
          onChange={handleChange}
          onBlur={field.handleBlur}
          placeholder="Enter pursuit title..."
          className="dark:bg-default-dark"
          aria-invalid={!!error}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </>
    )
  }

  const PursuitDescriptionFieldControl: React.FC = () => {
    const field = useFieldContext<string | undefined>()
    const rawError = useStore(field.store, selectRawError)
    const error = EditField.formatErrorMessage(rawError)
    return (
      <RichTextDescriptionField
        value={field.state.value}
        onChange={field.handleChange}
        error={error}
      />
    )
  }

  const StageFieldControl: React.FC = () => {
    const field = useFieldContext<string | undefined>()
    const rawError = useStore(field.store, selectRawError)
    const error = EditField.formatErrorMessage(rawError)
    const handleChange = useCallback(
      (value: UUID) => {
        field.handleChange(value)
      },
      [field],
    )
    return (
      <div className="flex flex-col gap-2">
        <StageSelect
          value={field.state.value as UUID}
          onChange={handleChange}
          triggerClassName={triggerClassName}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </div>
    )
  }

  const TypeFieldControl: React.FC = () => {
    const field = useFieldContext<PursuitType | undefined>()
    const rawError = useStore(field.store, selectRawError)
    const error = EditField.formatErrorMessage(rawError)
    const handleChange = useCallback(
      (value: PursuitType) => {
        field.handleChange(value)
      },
      [field],
    )
    return (
      <div className="flex flex-col gap-2">
        <TypeSelect
          value={field.state.value || 'federal_contract_pursuit'}
          onChange={handleChange}
          triggerClassName={triggerClassName}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </div>
    )
  }

  const AssigneesFieldControl: React.FC = () => {
    const field = useFieldContext<string[] | undefined>()
    const rawError = useStore(field.store, selectRawError)
    const error = EditField.formatErrorMessage(rawError)
    const handleChange = useCallback(
      (value: string[]) => {
        field.handleChange(value)
      },
      [field],
    )
    return (
      <div className="flex flex-col gap-2">
        <AssigneeComboboxMulti
          value={field.state.value ?? []}
          onChange={handleChange}
          triggerClassName={triggerClassName}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </div>
    )
  }

  const DueDateFieldControl: React.FC = () => {
    const field = useFieldContext<Date | undefined>()
    const rawError = useStore(field.store, selectRawError)
    const error = EditField.formatErrorMessage(rawError)
    const handleChange = useCallback(
      (value: Date | null) => {
        field.handleChange(value ?? undefined)
      },
      [field],
    )
    return (
      <div className="flex flex-col gap-2">
        <DateSelect
          value={field.state.value ?? null}
          onChange={handleChange}
          triggerClassName={triggerClassName}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </div>
    )
  }

  const TagsFieldControl: React.FC = () => {
    const field = useFieldContext<UUID[] | undefined>()
    const rawError = useStore(field.store, selectRawError)
    const error = EditField.formatErrorMessage(rawError)
    const handleChange = useCallback(
      (value: UUID[]) => {
        field.handleChange(value)
      },
      [field],
    )
    return (
      <div className="flex flex-col gap-2">
        <TagSelect
          value={field.state.value ?? []}
          onChange={handleChange}
          triggerClassName={triggerClassName}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </div>
    )
  }

  const EstimatedValueFieldControl: React.FC = () => {
    const field = useFieldContext<number | null | undefined>()
    const rawError = useStore(field.store, selectRawError)
    const error = EditField.formatErrorMessage(rawError)

    const [text, setText] = useState<string>(() => {
      const v = field.state.value
      return v != null ? formatWithCommas(v) : ''
    })

    const handleFocus = useCallback(() => {
      // Strip commas on focus so editing doesn't cause cursor jumps
      setText((prev) => prev.replace(/,/g, ''))
    }, [])

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const sanitized = sanitizeDollarInput(e.target.value)
        setText(sanitized)
        const num = Number(sanitized)
        field.handleChange(
          sanitized === '' || !Number.isFinite(num) ? null : num,
        )
      },
      [field],
    )

    const handleBlur = useCallback(() => {
      const raw = text.replace(/[^0-9.]/g, '')
      if (raw === '' || raw === '.') {
        setText('')
        field.handleChange(null)
      } else {
        const num = Number(raw)
        setText(Number.isFinite(num) ? formatWithCommas(num) : '')
      }
      field.handleBlur()
    }, [text, field])

    return (
      <>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-primary-400 dark:text-primary-500 pointer-events-none">
            $
          </span>
          <FormInput
            type="text"
            inputMode="decimal"
            value={text}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="0"
            className="pl-7 dark:bg-default-dark tabular-nums"
            aria-invalid={!!error}
          />
        </div>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </>
    )
  }

  return {
    TitleFieldControl,
    PursuitDescriptionFieldControl,
    StageFieldControl,
    TypeFieldControl,
    AssigneesFieldControl,
    DueDateFieldControl,
    TagsFieldControl,
    EstimatedValueFieldControl,
  }
}
